'use client';

import { useState, useEffect } from 'react';

import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import Icon from '@/components/ui/AppIcon';
import AppLogo from '@/components/ui/AppLogo';

type OverallStatus = 'operational' | 'degraded' | 'partial-outage' | 'major-outage';
type ComponentStatus = 'operational' | 'degraded' | 'partial-outage' | 'major-outage' | 'maintenance';
type View = 'public' | 'admin';

interface Component {
  id: string;
  name: string;
  status: ComponentStatus;
  uptime: number;
}

interface Incident {
  id: string;
  title: string;
  severity: 'investigating' | 'identified' | 'monitoring' | 'resolved';
  components: string[];
  startedAt: string;
  updates: { time: string; message: string; status: string }[];
  resolved: boolean;
}

interface MaintenanceWindow {
  id: string;
  title: string;
  description: string;
  components: string[];
  scheduledStart: string;
  scheduledEnd: string;
}

const initialComponents: Component[] = [
  { id: 'api-core', name: 'API (Core)', status: 'operational', uptime: 99.97 },
  { id: 'auth', name: 'Authentication', status: 'operational', uptime: 99.99 },
  { id: 'database', name: 'Database', status: 'operational', uptime: 99.95 },
  { id: 'ai-openai', name: 'AI Features — OpenAI', status: 'degraded', uptime: 99.82 },
  { id: 'ai-claude', name: 'AI Features — Claude/Anthropic', status: 'operational', uptime: 99.91 },
  { id: 'email', name: 'Email Service (Resend)', status: 'operational', uptime: 99.98 },
  { id: 'webhooks', name: 'Webhook Processing', status: 'operational', uptime: 99.93 },
  { id: 'dashboard', name: 'Dashboard & UI', status: 'operational', uptime: 99.99 },
  { id: 'storage', name: 'File Storage', status: 'operational', uptime: 99.96 },
];

const mockIncidents: Incident[] = [
  {
    id: '1',
    title: 'Elevated API response times for OpenAI integration',
    severity: 'monitoring',
    components: ['AI Features — OpenAI'],
    startedAt: '2026-03-05 08:14 UTC',
    resolved: false,
    updates: [
      { time: '10:32 UTC', message: 'We have identified the root cause as increased latency from OpenAI upstream. Our team has implemented request queuing to mitigate impact.', status: 'Monitoring' },
      { time: '09:15 UTC', message: 'We have identified the issue as elevated response times from our OpenAI API integration. AI features remain functional but may be slower than usual.', status: 'Identified' },
      { time: '08:14 UTC', message: 'We are investigating reports of slow AI feature responses.', status: 'Investigating' },
    ],
  },
];

const mockMaintenance: MaintenanceWindow[] = [
  {
    id: '1',
    title: 'Database Index Optimization',
    description: 'Scheduled maintenance to optimize database query performance. No downtime expected.',
    components: ['Database'],
    scheduledStart: '2026-03-08 02:00 UTC',
    scheduledEnd: '2026-03-08 04:00 UTC',
  },
];

const mockHistory: Incident[] = [
  {
    id: 'h1',
    title: 'Authentication service intermittent failures',
    severity: 'resolved',
    components: ['Authentication'],
    startedAt: '2026-02-20 14:22 UTC',
    resolved: true,
    updates: [
      { time: '15:45 UTC', message: 'Issue fully resolved. Root cause: expired SSL certificate on auth service. Certificate renewed and monitoring confirmed stable.', status: 'Resolved' },
      { time: '14:50 UTC', message: 'Fix deployed. Monitoring for stability.', status: 'Monitoring' },
      { time: '14:22 UTC', message: 'Investigating authentication failures reported by users.', status: 'Investigating' },
    ],
  },
];

const statusConfig: Record<ComponentStatus, { label: string; color: string; bg: string; dot: string }> = {
  operational: { label: 'Operational', color: 'text-emerald-400', bg: 'bg-emerald-400/10', dot: 'bg-emerald-400' },
  degraded: { label: 'Degraded Performance', color: 'text-amber-400', bg: 'bg-amber-400/10', dot: 'bg-amber-400' },
  'partial-outage': { label: 'Partial Outage', color: 'text-orange-400', bg: 'bg-orange-400/10', dot: 'bg-orange-400' },
  'major-outage': { label: 'Major Outage', color: 'text-red-400', bg: 'bg-red-400/10', dot: 'bg-red-400' },
  maintenance: { label: 'Under Maintenance', color: 'text-blue-400', bg: 'bg-blue-400/10', dot: 'bg-blue-400' },
};

const overallStatusConfig: Record<OverallStatus, { label: string; bg: string; border: string; text: string; icon: string }> = {
  operational: { label: 'All Systems Operational', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', icon: 'CheckCircleIcon' },
  degraded: { label: 'Degraded Performance', bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', icon: 'ExclamationTriangleIcon' },
  'partial-outage': { label: 'Partial Outage', bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400', icon: 'ExclamationCircleIcon' },
  'major-outage': { label: 'Major Outage', bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', icon: 'XCircleIcon' },
};

function generate90DayBlocks(uptime: number) {
  return Array.from({ length: 90 }, (_, i) => {
    const rand = Math.random();
    if (uptime > 99.9) return rand < 0.02 ? 'degraded' : 'operational';
    if (uptime > 99.5) return rand < 0.05 ? (rand < 0.02 ? 'outage' : 'degraded') : 'operational';
    return rand < 0.08 ? (rand < 0.03 ? 'outage' : 'degraded') : 'operational';
  });
}

export default function SLAPublicStatusPage() {
  const [view, setView] = useState<View>('public');
  const [components, setComponents] = useState<Component[]>(initialComponents);
  const [incidents, setIncidents] = useState<Incident[]>(mockIncidents);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [showNewIncident, setShowNewIncident] = useState(false);
  const [newIncident, setNewIncident] = useState({ title: '', description: '', severity: 'investigating', components: [] as string[] });
  const [activeIncidentUpdate, setActiveIncidentUpdate] = useState<string | null>(null);
  const [updateMessage, setUpdateMessage] = useState('');
  const [updateStatus, setUpdateStatus] = useState('monitoring');

  const overallStatus: OverallStatus = components.some(c => c.status === 'major-outage') ? 'major-outage'
    : components.some(c => c.status === 'partial-outage') ? 'partial-outage'
    : components.some(c => c.status === 'degraded') ? 'degraded'
    : 'operational';

  useEffect(() => {
    const interval = setInterval(() => setLastUpdated(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  const overallCfg = overallStatusConfig[overallStatus];

  const slaMetrics = [
    { label: 'Current Month Uptime', value: '99.94%', target: '99.9%', met: true },
    { label: 'P95 API Response Time', value: '287ms', target: '< 500ms', met: true },
    { label: 'Error Rate', value: '0.06%', target: '< 0.1%', met: true },
    { label: 'Incidents This Month', value: '2', target: '—', met: true },
    { label: 'Mean Time to Resolution', value: '1h 23m', target: '—', met: true },
  ];

  const responseTimeData = Array.from({ length: 30 }, (_, i) => ({
    day: i + 1,
    value: 200 + Math.floor(Math.random() * 150),
  }));

  const maxRT = Math.max(...responseTimeData.map(d => d.value));

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 pb-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">

          {/* View Toggle */}
          <div className="flex justify-end mb-6">
            <div className="flex rounded-lg border border-border overflow-hidden">
              <button
                onClick={() => setView('public')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  view === 'public' ? 'bg-[#C9A961] text-black' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Public View
              </button>
              <button
                onClick={() => setView('admin')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  view === 'admin' ? 'bg-[#C9A961] text-black' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Admin View
              </button>
            </div>
          </div>

          {/* ===== PUBLIC VIEW ===== */}
          {view === 'public' && (
            <div>
              {/* Branding Header */}
              <div className="text-center mb-8">
                <div className="flex items-center justify-center gap-3 mb-3">
                  <AppLogo src="/assets/images/Untitled_design-27-1772027045051.png" size={48} />
                  <h1 className="text-3xl font-bold text-foreground">ChronoMind™ Status</h1>
                </div>
                <p className="text-muted-foreground">Real-time system health and incident updates</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Last updated: {lastUpdated.toLocaleTimeString()} · Auto-refreshes every 60 seconds
                </p>
              </div>

              {/* Overall Status Banner */}
              <div className={`rounded-2xl border ${overallCfg.border} ${overallCfg.bg} p-5 mb-8 flex items-center gap-4`}>
                <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full ${overallCfg.bg} border ${overallCfg.border}`}>
                  <Icon name={overallCfg.icon} className={`h-6 w-6 ${overallCfg.text}`} />
                </div>
                <div>
                  <h2 className={`text-xl font-bold ${overallCfg.text}`}>{overallCfg.label}</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {overallStatus === 'operational' ?'All ChronoMind™ services are operating normally.' :'Some services are experiencing issues. See details below.'}
                  </p>
                </div>
              </div>

              {/* Active Incidents */}
              {incidents.filter(i => !i.resolved).length > 0 && (
                <div className="mb-8">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Active Incidents</h2>
                  {incidents.filter(i => !i.resolved).map(incident => (
                    <div key={incident.id} className="rounded-xl border border-amber-400/30 bg-amber-400/5 p-5 mb-4">
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div>
                          <h3 className="font-semibold text-foreground">{incident.title}</h3>
                          <p className="text-sm text-muted-foreground mt-0.5">Started: {incident.startedAt}</p>
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {incident.components.map(c => (
                              <span key={c} className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">{c}</span>
                            ))}
                          </div>
                        </div>
                        <span className="flex-shrink-0 inline-flex items-center rounded-full bg-amber-400/10 border border-amber-400/30 px-3 py-1 text-xs font-medium text-amber-400 capitalize">
                          {incident.severity}
                        </span>
                      </div>
                      <div className="space-y-3 border-t border-border pt-3">
                        {incident.updates.map((update, idx) => (
                          <div key={idx} className="flex gap-3">
                            <div className="flex flex-col items-center">
                              <div className="h-2 w-2 rounded-full bg-[#C9A961] mt-1.5 flex-shrink-0" />
                              {idx < incident.updates.length - 1 && <div className="w-px flex-1 bg-border mt-1" />}
                            </div>
                            <div className="pb-2">
                              <p className="text-xs font-medium text-[#C9A961]">{update.status} · {update.time}</p>
                              <p className="text-sm text-foreground mt-0.5">{update.message}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Scheduled Maintenance */}
              {mockMaintenance.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Scheduled Maintenance</h2>
                  {mockMaintenance.map(mw => (
                    <div key={mw.id} className="rounded-xl border border-blue-400/30 bg-blue-400/5 p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-semibold text-foreground">{mw.title}</h3>
                          <p className="text-sm text-muted-foreground mt-1">{mw.description}</p>
                          <p className="text-sm text-blue-400 mt-2">{mw.scheduledStart} → {mw.scheduledEnd}</p>
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {mw.components.map(c => (
                              <span key={c} className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">{c}</span>
                            ))}
                          </div>
                        </div>
                        <span className="flex-shrink-0 inline-flex items-center rounded-full bg-blue-400/10 border border-blue-400/30 px-3 py-1 text-xs font-medium text-blue-400">Scheduled</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Component Status Grid */}
              <div className="mb-8">
                <h2 className="text-lg font-semibold text-foreground mb-4">Component Status</h2>
                <div className="rounded-xl border border-border bg-card overflow-hidden">
                  <div className="divide-y divide-border">
                    {components.map(comp => {
                      const cfg = statusConfig[comp.status];
                      const blocks = generate90DayBlocks(comp.uptime);
                      return (
                        <div key={comp.id} className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className={`h-2.5 w-2.5 rounded-full ${cfg.dot} flex-shrink-0`} />
                              <span className="text-sm font-medium text-foreground">{comp.name}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-muted-foreground">{comp.uptime}% uptime</span>
                              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cfg.bg} ${cfg.color}`}>
                                {cfg.label}
                              </span>
                            </div>
                          </div>
                          {/* 90-day blocks */}
                          <div className="flex gap-0.5 mt-2">
                            {blocks.map((b, i) => (
                              <div
                                key={i}
                                title={`Day ${i + 1}: ${b}`}
                                className={`h-6 flex-1 rounded-sm ${
                                  b === 'operational' ? 'bg-emerald-500' :
                                  b === 'degraded'? 'bg-amber-500' : 'bg-red-500'
                                } opacity-80 hover:opacity-100 transition-opacity cursor-default`}
                              />
                            ))}
                          </div>
                          <div className="flex justify-between mt-1">
                            <span className="text-xs text-muted-foreground">90 days ago</span>
                            <span className="text-xs text-muted-foreground">Today</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Response Time Graph */}
              <div className="mb-8">
                <h2 className="text-lg font-semibold text-foreground mb-4">API Response Time — Last 30 Days</h2>
                <div className="rounded-xl border border-border bg-card p-5">
                  <div className="flex items-end gap-1 h-32">
                    {responseTimeData.map((d, i) => (
                      <div
                        key={i}
                        title={`Day ${d.day}: ${d.value}ms`}
                        className="flex-1 rounded-t-sm bg-[#C9A961]/60 hover:bg-[#C9A961] transition-colors cursor-default"
                        style={{ height: `${(d.value / maxRT) * 100}%` }}
                      />
                    ))}
                  </div>
                  <div className="flex justify-between mt-2">
                    <span className="text-xs text-muted-foreground">30 days ago</span>
                    <span className="text-xs text-muted-foreground">Avg: ~{Math.round(responseTimeData.reduce((a, b) => a + b.value, 0) / 30)}ms · Target: &lt;500ms</span>
                    <span className="text-xs text-muted-foreground">Today</span>
                  </div>
                </div>
              </div>

              {/* Incident History */}
              <div className="mb-8">
                <h2 className="text-lg font-semibold text-foreground mb-4">Incident History</h2>
                {mockHistory.map(incident => (
                  <div key={incident.id} className="rounded-xl border border-border bg-card p-5 mb-4">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div>
                        <h3 className="font-semibold text-foreground">{incident.title}</h3>
                        <p className="text-sm text-muted-foreground mt-0.5">{incident.startedAt}</p>
                      </div>
                      <span className="flex-shrink-0 inline-flex items-center rounded-full bg-emerald-400/10 border border-emerald-400/30 px-3 py-1 text-xs font-medium text-emerald-400">Resolved</span>
                    </div>
                    <div className="space-y-2 border-t border-border pt-3">
                      {incident.updates.map((update, idx) => (
                        <div key={idx} className="flex gap-3">
                          <div className="h-2 w-2 rounded-full bg-muted-foreground mt-1.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-medium text-muted-foreground">{update.status} · {update.time}</p>
                            <p className="text-sm text-foreground mt-0.5">{update.message}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Subscribe to Updates */}
              <div className="rounded-xl border border-border bg-card p-6 mb-8">
                <h2 className="text-lg font-semibold text-foreground mb-2">Subscribe to Status Updates</h2>
                <p className="text-sm text-muted-foreground mb-4">Receive email notifications when incidents are created or resolved.</p>
                {subscribed ? (
                  <div className="flex items-center gap-2 text-emerald-400">
                    <Icon name="CheckCircleIcon" className="h-5 w-5" />
                    <span className="text-sm font-medium">You&apos;re subscribed! We&apos;ll notify you of any status changes.</span>
                  </div>
                ) : (
                  <div className="flex gap-3">
                    <input
                      type="email"
                      value={subscribeEmail}
                      onChange={e => setSubscribeEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[#C9A961]/50"
                    />
                    <button
                      onClick={() => { if (subscribeEmail) setSubscribed(true); }}
                      className="rounded-lg bg-[#C9A961] px-4 py-2 text-sm font-semibold text-black hover:bg-[#C9A961]/90 transition-colors"
                    >
                      Subscribe
                    </button>
                  </div>
                )}
              </div>

              {/* Status Badge */}
              <div className="rounded-xl border border-border bg-card p-5">
                <h2 className="text-base font-semibold text-foreground mb-2">Embeddable Status Badge</h2>
                <p className="text-sm text-muted-foreground mb-3">Add this badge to your marketing site or documentation.</p>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 ${overallCfg.border} ${overallCfg.bg}`}>
                    <div className={`h-2 w-2 rounded-full ${overallStatus === 'operational' ? 'bg-emerald-400' : 'bg-amber-400'} animate-pulse`} />
                    <span className={`text-xs font-semibold ${overallCfg.text}`}>ChronoMind™ {overallCfg.label}</span>
                  </div>
                  <code className="rounded-md bg-muted px-3 py-1.5 text-xs text-muted-foreground font-mono">
                    {`<img src="https://chronomind7221.builtwithrocket.new/status/badge.svg" />`}
                  </code>
                </div>
              </div>
            </div>
          )}

          {/* ===== ADMIN VIEW ===== */}
          {view === 'admin' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-foreground">Incident Management</h2>
                <button
                  onClick={() => setShowNewIncident(true)}
                  className="flex items-center gap-2 rounded-lg bg-[#C9A961] px-4 py-2 text-sm font-semibold text-black hover:bg-[#C9A961]/90 transition-colors"
                >
                  <Icon name="PlusIcon" className="h-4 w-4" />
                  Create Incident
                </button>
              </div>

              {/* SLA Metrics Dashboard */}
              <div className="mb-8">
                <h3 className="text-base font-semibold text-foreground mb-4">SLA Metrics — March 2026</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                  {slaMetrics.map(metric => (
                    <div key={metric.label} className={`rounded-xl border p-4 ${
                      metric.met ? 'border-emerald-400/20 bg-emerald-400/5' : 'border-red-400/20 bg-red-400/5'
                    }`}>
                      <p className={`text-xl font-bold ${metric.met ? 'text-emerald-400' : 'text-red-400'}`}>{metric.value}</p>
                      <p className="text-xs text-muted-foreground mt-1">{metric.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Target: {metric.target}</p>
                      <div className={`mt-2 flex items-center gap-1 text-xs font-medium ${metric.met ? 'text-emerald-400' : 'text-red-400'}`}>
                        <Icon name={metric.met ? 'CheckCircleIcon' : 'XCircleIcon'} className="h-3.5 w-3.5" />
                        {metric.met ? 'Met' : 'Breached'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Component Status Override */}
              <div className="mb-8">
                <h3 className="text-base font-semibold text-foreground mb-4">Component Status Override</h3>
                <div className="rounded-xl border border-border bg-card overflow-hidden">
                  <div className="divide-y divide-border">
                    {components.map(comp => (
                      <div key={comp.id} className="flex items-center justify-between p-4">
                        <div className="flex items-center gap-2">
                          <div className={`h-2.5 w-2.5 rounded-full ${statusConfig[comp.status].dot}`} />
                          <span className="text-sm font-medium text-foreground">{comp.name}</span>
                        </div>
                        <select
                          value={comp.status}
                          onChange={e => setComponents(prev => prev.map(c => c.id === comp.id ? { ...c, status: e.target.value as ComponentStatus } : c))}
                          className="rounded-lg border border-border bg-background px-3 py-1.5 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-[#C9A961]/50"
                        >
                          <option value="operational">Operational</option>
                          <option value="degraded">Degraded Performance</option>
                          <option value="partial-outage">Partial Outage</option>
                          <option value="major-outage">Major Outage</option>
                          <option value="maintenance">Under Maintenance</option>
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Active Incidents Management */}
              <div className="mb-8">
                <h3 className="text-base font-semibold text-foreground mb-4">Active Incidents</h3>
                {incidents.filter(i => !i.resolved).length === 0 ? (
                  <div className="rounded-xl border border-border bg-card p-8 text-center">
                    <Icon name="CheckCircleIcon" className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
                    <p className="text-foreground font-medium">No active incidents</p>
                    <p className="text-sm text-muted-foreground mt-1">All systems are operating normally.</p>
                  </div>
                ) : (
                  incidents.filter(i => !i.resolved).map(incident => (
                    <div key={incident.id} className="rounded-xl border border-amber-400/30 bg-card p-5 mb-4">
                      <div className="flex items-start justify-between gap-4 mb-4">
                        <div>
                          <h4 className="font-semibold text-foreground">{incident.title}</h4>
                          <p className="text-sm text-muted-foreground mt-0.5">Started: {incident.startedAt}</p>
                        </div>
                        <span className="flex-shrink-0 inline-flex items-center rounded-full bg-amber-400/10 border border-amber-400/30 px-3 py-1 text-xs font-medium text-amber-400 capitalize">
                          {incident.severity}
                        </span>
                      </div>
                      <div className="flex gap-3 flex-wrap">
                        <button
                          onClick={() => setActiveIncidentUpdate(activeIncidentUpdate === incident.id ? null : incident.id)}
                          className="flex items-center gap-1.5 rounded-lg border border-[#C9A961]/30 px-3 py-1.5 text-xs font-medium text-[#C9A961] hover:bg-[#C9A961]/10 transition-colors"
                        >
                          <Icon name="PencilIcon" className="h-3.5 w-3.5" />
                          Add Update
                        </button>
                        <button
                          onClick={() => setIncidents(prev => prev.map(i => i.id === incident.id ? { ...i, resolved: true, severity: 'resolved' as any } : i))}
                          className="flex items-center gap-1.5 rounded-lg border border-emerald-400/30 px-3 py-1.5 text-xs font-medium text-emerald-400 hover:bg-emerald-400/10 transition-colors"
                        >
                          <Icon name="CheckCircleIcon" className="h-3.5 w-3.5" />
                          Resolve Incident
                        </button>
                      </div>
                      {activeIncidentUpdate === incident.id && (
                        <div className="mt-4 space-y-3 border-t border-border pt-4">
                          <select
                            value={updateStatus}
                            onChange={e => setUpdateStatus(e.target.value)}
                            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-[#C9A961]/50"
                          >
                            <option value="investigating">Investigating</option>
                            <option value="identified">Identified</option>
                            <option value="monitoring">Monitoring</option>
                            <option value="resolved">Resolved</option>
                          </select>
                          <textarea
                            value={updateMessage}
                            onChange={e => setUpdateMessage(e.target.value)}
                            rows={3}
                            placeholder="Describe the latest status update..."
                            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[#C9A961]/50 resize-none"
                          />
                          <button
                            onClick={() => {
                              if (updateMessage) {
                                setIncidents(prev => prev.map(i => i.id === incident.id ? {
                                  ...i,
                                  severity: updateStatus as any,
                                  updates: [{ time: new Date().toLocaleTimeString() + ' UTC', message: updateMessage, status: updateStatus.charAt(0).toUpperCase() + updateStatus.slice(1) }, ...i.updates]
                                } : i));
                                setUpdateMessage('');
                                setActiveIncidentUpdate(null);
                              }
                            }}
                            className="rounded-lg bg-[#C9A961] px-4 py-2 text-sm font-semibold text-black hover:bg-[#C9A961]/90 transition-colors"
                          >
                            Post Update
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Monthly SLA Report */}
              <div className="rounded-xl border border-border bg-card p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-semibold text-foreground">Monthly SLA Report</h3>
                  <button className="flex items-center gap-2 rounded-lg border border-[#C9A961]/30 px-4 py-2 text-sm font-medium text-[#C9A961] hover:bg-[#C9A961]/10 transition-colors">
                    <Icon name="ArrowDownTrayIcon" className="h-4 w-4" />
                    Export PDF
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {components.slice(0, 6).map(comp => (
                    <div key={comp.id} className="rounded-lg border border-border p-3">
                      <p className="text-xs text-muted-foreground mb-1">{comp.name}</p>
                      <p className="text-lg font-bold text-[#C9A961]">{comp.uptime}%</p>
                      <div className="h-1.5 rounded-full bg-muted mt-2 overflow-hidden">
                        <div className="h-full rounded-full bg-emerald-500" style={{ width: `${comp.uptime}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Create Incident Modal */}
      {showNewIncident && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-lg rounded-2xl border border-border bg-card p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Create New Incident</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Title</label>
                <input
                  type="text"
                  value={newIncident.title}
                  onChange={e => setNewIncident(p => ({ ...p, title: e.target.value }))}
                  placeholder="Brief description of the incident"
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[#C9A961]/50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Initial Status</label>
                <select
                  value={newIncident.severity}
                  onChange={e => setNewIncident(p => ({ ...p, severity: e.target.value }))}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[#C9A961]/50"
                >
                  <option value="investigating">Investigating</option>
                  <option value="identified">Identified</option>
                  <option value="monitoring">Monitoring</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Affected Components</label>
                <div className="grid grid-cols-2 gap-2">
                  {components.map(comp => (
                    <label key={comp.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newIncident.components.includes(comp.name)}
                        onChange={() => {
                          const updated = newIncident.components.includes(comp.name)
                            ? newIncident.components.filter(c => c !== comp.name)
                            : [...newIncident.components, comp.name];
                          setNewIncident(p => ({ ...p, components: updated }));
                        }}
                        className="rounded border-border"
                      />
                      <span className="text-sm text-foreground">{comp.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Initial Message</label>
                <textarea
                  value={newIncident.description}
                  onChange={e => setNewIncident(p => ({ ...p, description: e.target.value }))}
                  rows={3}
                  placeholder="Describe what is happening..."
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[#C9A961]/50 resize-none"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  if (newIncident.title) {
                    const incident: Incident = {
                      id: String(Date.now()),
                      title: newIncident.title,
                      severity: newIncident.severity as any,
                      components: newIncident.components,
                      startedAt: new Date().toISOString().replace('T', ' ').slice(0, 16) + ' UTC',
                      resolved: false,
                      updates: [{ time: new Date().toLocaleTimeString() + ' UTC', message: newIncident.description || 'Incident created.', status: newIncident.severity.charAt(0).toUpperCase() + newIncident.severity.slice(1) }],
                    };
                    setIncidents(prev => [incident, ...prev]);
                    setShowNewIncident(false);
                    setNewIncident({ title: '', description: '', severity: 'investigating', components: [] });
                  }
                }}
                className="flex-1 rounded-lg bg-[#C9A961] px-4 py-2.5 text-sm font-semibold text-black hover:bg-[#C9A961]/90 transition-colors"
              >
                Create Incident
              </button>
              <button onClick={() => setShowNewIncident(false)} className="rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted/50 transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
